import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState } from 'react';
import { createApiUrl } from '../../../config/api';

interface TableData {
  [key: string]: any;
}

interface DataTableProps {
  tableFileName: string;
  sessionID?: string;
  userID?: string;
  refreshKey?: number; // Add optional refresh key prop
}

const DataTable: React.FC<DataTableProps> = ({ tableFileName, sessionID, userID, refreshKey }) => {
  const [data, setData] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; column: string } | null>(null);
  const [editedValue, setEditedValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFetchedRef = useRef(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const headerRowRef = useRef<HTMLTableRowElement>(null);

  // Virtual scrolling setup - MUST be called unconditionally before any early returns
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45,
    overscan: 10,
  });

  // Measure column widths from header after data loads
  useEffect(() => {
    if (data.length > 0 && headerRowRef.current) {
      const cells = headerRowRef.current.querySelectorAll('th');
      const widths: Record<string, number> = {};
      const columns = Object.keys(data[0]);
      
      cells.forEach((cell, index) => {
        if (columns[index]) {
          widths[columns[index]] = cell.offsetWidth;
        }
      });
      
      setColumnWidths(widths);
    }
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Set loading to true when refreshing
      setEditingCell(null); // Cancel any active editing when refreshing
      hasFetchedRef.current = false;
      console.log('[DataTable] Fetching data for file:', tableFileName);
      console.log('[DataTable] Refresh triggered by refreshKey:', refreshKey);
      try {
        const url = createApiUrl(`/file_handling/users/${userID}/sessions/${sessionID}/files/processed/file_types/table/${tableFileName}`);
        console.log('[DataTable] Making request to:', url);
        
        const response = await fetch(url);
        console.log('[DataTable] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[DataTable] Error response:', errorText);
          throw new Error('Failed to fetch table data');
        }
        
        const jsonData = await response.json();
        console.log('[DataTable] Received data:', jsonData);
        setData(jsonData);
      } catch (err) {
        console.error('[DataTable] Error:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tableFileName, sessionID, userID, refreshKey]); // Add refreshKey to dependencies

  const saveChanges = (cellToSave: { rowIndex: number; column: string } | null, valueToSave: string) => {
    if (!cellToSave) return;

    try {
      const newData = [...data];
      newData[cellToSave.rowIndex] = {
        ...newData[cellToSave.rowIndex],
        [cellToSave.column]: valueToSave
      };
      setData(newData);
    } catch (err) {
      console.error('Error saving cell:', err);
      setError('Failed to save changes');
    }
    setEditingCell(null);
  };

  const handleCellClick = (rowIndex: number, column: string, value: any) => {
    // If we're already editing a cell, save its changes first
    if (editingCell) {
      saveChanges(editingCell, editedValue);
    }
    
    // Then start editing the new cell
    setEditingCell({ rowIndex, column });
    setEditedValue(String(value));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveChanges(editingCell, editedValue);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // Handle clicks outside the editing cell
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editingCell && inputRef.current && !inputRef.current.contains(e.target as Node)) {
        saveChanges(editingCell, editedValue);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingCell, editedValue, data]);

  const handleSaveTable = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    
    try {      console.log('Saving table data:', JSON.stringify({
          file_name: tableFileName,
          session_id: sessionID,
          user_id: userID,
        }));
      const response = await fetch(createApiUrl('/file_handling/table/save'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: data,
          file_name: tableFileName,
          session_id: sessionID,
          user_id: userID,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save table: ${errorText}`);
      }

      const result = await response.json();
      console.log('Table saved successfully:', result);
    } catch (err) {
      console.error('Error saving table:', err);
      setError(err instanceof Error ? err.message : 'Failed to save table');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading table data...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  if (data.length === 0) {
    return <div className="p-4">No data available</div>;
  }

  const columns = Object.keys(data[0]);

  return (
    <div className="w-full h-full flex flex-col">
      {/* Modern Header with Save Table button */}
      <div className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-white to-gray-50/50 border-b border-gray-200/60 backdrop-blur-sm select-none">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-6 rounded-full" style={{ backgroundColor: 'rgb(30, 156, 215)' }}></div>
            <h2 
            className="text-xl font-light tracking-wide" 
            style={{ 
              fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif', 
              color: 'rgb(30, 156, 215)',
              letterSpacing: '0.5px'
            }}
            >
            Table: {tableFileName.replace(/\.[^/.]+$/, '')}
            </h2>
        </div>
        <button
          onClick={handleSaveTable}
          disabled={isSaving}
          className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-300 select-none focus:outline-none focus:ring-2 focus:ring-blue-200 ${
            isSaving 
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-inner' 
              : 'text-white hover:shadow-lg hover:scale-105 cursor-pointer transform active:scale-95'
          }`}
          style={{ 
            background: isSaving 
              ? undefined 
              : 'linear-gradient(135deg, rgba(30, 156, 215, 0.8) 0%, rgba(30, 156, 215, 0.9) 100%)',
            userSelect: 'none',
            boxShadow: isSaving 
              ? undefined 
              : '0 4px 15px rgba(30, 156, 215, 0.2)'
          }}
        >
          {isSaving ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Saving...</span>
            </div>
          ) : (
            'Save Table'
          )}
        </button>
      </div>

      <div className="flex-1 p-6 select-none relative min-h-0">
        {/* Scrollable table container with modern styling and left-side scrollbar */}
        <div 
          className="bg-white rounded-2xl shadow-lg border border-gray-100 select-none relative h-full"
          style={{
            overflow: 'auto',
            direction: 'rtl' // This moves scrollbar to the left
          }}
        >
          {/* Custom scrollbar styles for webkit browsers - left side, auto-hide, light grey */}
          <style>{`
            .modern-scrollbar {
              scrollbar-width: thin;
              scrollbar-color: transparent transparent;
              transition: scrollbar-color 0.3s ease;
            }
            
            .modern-scrollbar:hover {
              scrollbar-color: rgba(156, 163, 175, 0.6) transparent;
            }
            
            .modern-scrollbar::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }
            
            .modern-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            
            .modern-scrollbar::-webkit-scrollbar-thumb {
              background: transparent;
              border-radius: 10px;
              transition: all 0.3s ease;
            }
            
            .modern-scrollbar:hover::-webkit-scrollbar-thumb {
              background: rgba(156, 163, 175, 0.6);
            }
            
            .modern-scrollbar::-webkit-scrollbar-thumb:hover {
              background: rgba(156, 163, 175, 0.8);
            }
            
            .modern-scrollbar::-webkit-scrollbar-corner {
              background: transparent;
            }
            
            /* Show scrollbar only when scrolling */
            @keyframes fadeInScrollbar {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes fadeOutScrollbar {
              from { opacity: 1; }
              to { opacity: 0; }
            }
            
            .modern-scrollbar::-webkit-scrollbar-thumb:active {
              background: rgba(156, 163, 175, 0.9);
            }
          `}</style>
          
          <div 
            ref={tableContainerRef}
            className="modern-scrollbar"
            style={{ 
              direction: 'ltr', // Reset direction for table content
              height: '100%',
              overflow: 'auto'
            }}
          >
            <table className="border-collapse w-full min-w-max">
              <thead className="sticky top-0 z-10">
                <tr ref={headerRowRef} className="bg-gradient-to-r from-gray-50 to-gray-100">
                  {columns.map((column) => (
                    <th
                      key={column}
                      className="px-4 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200 first:rounded-tl-2xl last:rounded-tr-2xl select-none backdrop-blur-sm"
                      style={{ 
                        minWidth: '150px',
                        width: columnWidths[column] ? `${columnWidths[column]}px` : 'auto',
                        maxWidth: columnWidths[column] ? `${columnWidths[column]}px` : 'none',
                        userSelect: 'none',
                        position: 'sticky',
                        top: 0,
                        background: 'linear-gradient(to right, rgb(249, 250, 251), rgb(243, 244, 246))',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const rowIndex = virtualRow.index;
                  const row = data[rowIndex];
                  
                  return (
                    <tr 
                      key={virtualRow.key}
                      className="hover:bg-gray-50/50 transition-colors duration-150"
                      style={{
                        display: 'flex',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: `${virtualRow.size}px`,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {columns.map((column) => (
                        <td
                          key={`${rowIndex}-${column}`}
                          className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100 cursor-pointer hover:bg-blue-50/50 transition-colors duration-150 select-none"
                          onClick={() => handleCellClick(rowIndex, column, row[column])}
                          style={{ 
                            minWidth: '150px',
                            width: columnWidths[column] ? `${columnWidths[column]}px` : '150px',
                            maxWidth: columnWidths[column] ? `${columnWidths[column]}px` : '150px',
                            flex: '0 0 auto',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            userSelect: 'none'
                          }}
                        >
                          {editingCell?.rowIndex === rowIndex && editingCell?.column === column ? (
                            <input
                              ref={inputRef}
                              type="text"
                              value={editedValue}
                              onChange={(e) => setEditedValue(e.target.value)}
                              onKeyDown={handleKeyDown}
                              className="border rounded-lg px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm select-text"
                              style={{ userSelect: 'text' }}
                              autoFocus
                            />
                          ) : (
                            <span className="block truncate select-none" style={{ userSelect: 'none' }}>{row[column]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;